import i18n from "../i18n";
import React from "react";
import { Box, Button, Image, Stack, Text, Title } from "@mantine/core";

import FileList from "./fileList";
import { getFileUrl, isImageFile, isPDFFile } from "./fileList";
import { isWidgetReadOnly } from "../helpers/formReadOnly";

const previewPanelSx = {
  border: "1px solid var(--mantine-color-dark-4)",
  borderRadius: "var(--mantine-radius-md)",
  overflow: "hidden",
  background: "var(--mantine-color-dark-7)",
  minHeight: 420,
  height: 420,
};

const getPdfViewerUrl = (url) => {
  const base = url.split("#")[0];
  return `${base}#navpanes=0&toolbar=1&scrollbar=1&view=FitH`;
};

const isPdfFile = (file) => {
  const name = file?.name?.toLowerCase?.() ?? "";
  const mime = file?.mimetype || file?.type || "";
  return isPDFFile(mime) || mime === "application/pdf" || name.endsWith(".pdf");
};

const CertificatePreview = ({ file }) => {
  const url = getFileUrl(file);

  if (!url) {
    return (
      <Box
        sx={{
          ...previewPanelSx,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Text size="sm" color="dimmed">
          Upload a certificate to preview
        </Text>
      </Box>
    );
  }

  if (isPdfFile(file)) {
    return (
      <Box sx={previewPanelSx}>
        <iframe
          src={getPdfViewerUrl(url)}
          title={file?.name || "Certificate PDF"}
          width="100%"
          height="100%"
          style={{ border: 0, display: "block", minHeight: 420 }}
        />
      </Box>
    );
  }

  if (isImageFile(file?.mimetype) || file?.type?.startsWith?.("image/")) {
    return (
      <Box sx={previewPanelSx}>
        <Image
          src={url}
          alt={file?.name || "Certificate"}
          fit="contain"
          height={420}
          styles={{
            root: { width: "100%", height: "100%" },
            image: { width: "100%", height: "100%", objectFit: "contain" },
          }}
        />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        ...previewPanelSx,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <Stack align="center" spacing="sm">
        <Text size="sm" color="dimmed">
          {file?.name}
        </Text>
        <Button
          component="a"
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          variant="light"
          size="xs"
        >
          Open file
        </Button>
      </Stack>
    </Box>
  );
};

const CpdFormCertificatePanel = ({
  form,
  name = "files",
  disabled = false,
  readOnly = false,
  readonly = false,
}) => {
  const files = form?.values?.[name] || [];
  const previewFile = files[0];
  const uploadDisabled = isWidgetReadOnly({
    disabled,
    readOnly,
    readonly,
    form,
  });

  return (
    <Stack spacing="md">
      <Title order={5}>{i18n.t("Certificate")}</Title>
      <CertificatePreview file={previewFile} />
      <FileList
        form={form}
        name={name}
        disabled={uploadDisabled}
        uploadLabel="Drag & drop certificate here"
        defaultDisplayMode={1}
        height={220}
        showScroll
      />
    </Stack>
  );
};

export default CpdFormCertificatePanel;
